import { createStore } from "@tellescope/store";
import type { InboxFilters } from "../pages/messaging/utils/inbox-filters";

export const ALL_INBOX_VIEW_ID = "all" as const;

export interface InboxViewStore {
  viewId: string;
  defaultViewFiltersByUserId: Record<string, InboxFilters>;
  setViewId: (viewId: string) => void;
  setDefaultViewFilters: (userId: string, filters: InboxFilters) => void;
  clearDefaultViewFilters: (userId: string) => void;
}

/**
 * The inbox's view selection and the filters staged against its default ("All") view.
 *
 * Both live in localStorage: the selection is a per-user preference that persists across sessions,
 * not part of a shared link — an inbox URL identifies a thread, and whoever opens it sees their own
 * selected view.
 */
export const { useStore: useInboxViewStore, useShallowStore: useInboxViewShallowStore } = createStore<InboxViewStore>(
  (set) => ({
    viewId: ALL_INBOX_VIEW_ID,
    defaultViewFiltersByUserId: {},
    setViewId: (viewId) => set({ viewId: viewId || ALL_INBOX_VIEW_ID }),
    setDefaultViewFilters: (userId, filters) =>
      set((state) => ({
        defaultViewFiltersByUserId: { ...state.defaultViewFiltersByUserId, [userId]: filters },
      })),
    clearDefaultViewFilters: (userId) =>
      set((state) => {
        if (!(userId in state.defaultViewFiltersByUserId)) return state;
        const { [userId]: _removed, ...rest } = state.defaultViewFiltersByUserId;
        return { defaultViewFiltersByUserId: rest };
      }),
  }),
  { persist: "localStorage", name: "INBOX_VIEW" }
);
